import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountUserPermissionsForm, { AccountUserPermissionsFormRef } from './AccountUserPermissionsForm';
import PermissionsSearchField from './PermissionsSearchField';
import useSaveAccountUserPermissions from '@/hooks/useApi/useSaveAccountUserPermissions';
import useFetchAccountUserPermissions from '@/hooks/useApi/useFetchAccountUserPermissions';
import useFetchUserRoleAssignments from '@/hooks/useApi/useFetchUserRoleAssignments';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

import { Button, Chip } from 'UI';
import styles from './AccountUserPermissionsDrawer.module.scss';

import {
  SideDrawer,
  Box,
  BodyText,
  Tooltip,
  MaterialSymbol,
  withStyles,
  type AccountUserPermissions as AccountUserPermissionsDrawer,
  type AccountUserRole,
} from 'vopay';

const cn = withStyles(styles);

type FC = FunctionComponent<AccountUserPermissionsDrawerProps>;

type AccountUserPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  emailAddress?: string;
  accountToken?: string;
  /**
   * Full account role list (with each role's permission string keys). Used
   * to resolve which permissions are role-locked for the current user.
   * Optional — when omitted, no permissions are locked.
   */
  availableRoles?: AccountUserRole[];
};

const AccountUserPermissionsDrawer: FC = ({
  isOpen,
  onClose,
  emailAddress,
  accountToken,
  availableRoles = [],
}) => {
  const { permissions, loading, fetchAccountUserPermissions } = useFetchAccountUserPermissions(accountToken);
  const { saveAccountUserPermissions } = useSaveAccountUserPermissions(accountToken);
  const {
    roles: assignedRoles,
    fetchUserRoleAssignments,
    reset: resetAssignedRoles,
  } = useFetchUserRoleAssignments(accountToken);
  const formRef = useRef<AccountUserPermissionsFormRef | null>(null);
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserPermissions' });

  // Track form state for button disabling
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [allEnabled, setAllEnabled] = useState(false);
  const isSearching = searchTerm.trim().length > 0;
  // The form is gated on BOTH the permissions fetch AND the role-assignment
  // fetch completing. Formik doesn't re-initialize values when initialValues
  // changes, so the form must mount with the correct lock set in hand.
  const [dataReady, setDataReady] = useState(false);

  // Build the set of permission keys inherited from this user's assigned
  // roles. Each role's `Permissions` is a string[] of permission keys; we
  // union across all roles.
  const lockedPermissions = useMemo(() => {
    const set = new Set<string>();
    for (const assigned of assignedRoles) {
      const fullRole = availableRoles.find((r) => r.RoleID === assigned.RoleID);
      if (!fullRole) continue;
      for (const permKey of fullRole.Permissions) set.add(permKey);
    }
    return set;
  }, [assignedRoles, availableRoles]);

  // Deps are intentionally limited to emailAddress and isOpen. The fetch
  // functions (fetchAccountUserPermissions, fetchUserRoleAssignments) use the
  // Direct Pattern (no useCallback) and recreate on every render — including
  // them would cause infinite loops. setLoading, resetAssignedRoles, t, and
  // alertDialog are stable and don't need to be listed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!emailAddress || !isOpen) {
      resetAssignedRoles();
      setDataReady(false);
      return;
    }

    const fetchAll = async () => {
      try {
        setLoading(true);
        setDataReady(false);
        // Permissions + role assignments are fetched in parallel — they're
        // independent and the drawer needs both before rendering.
        await Promise.all([
          fetchAccountUserPermissions({ EmailAddress: emailAddress }),
          fetchUserRoleAssignments(emailAddress).catch(() => {
            // Already logged in the hook. Treat as no roles assigned.
          }),
        ]);
        setDataReady(true);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.fetchPermissions');
        alertDialog.error(t('errors.title'), errorMessage);
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [emailAddress, isOpen]);

  const handleSave = async (values: Record<string, boolean>) => {
    if (!emailAddress) return;

    try {
      setLoading(true);
      const params = { EmailAddress: emailAddress, ...(values as AccountUserPermissionsDrawer) };
      await saveAccountUserPermissions(params);
      onClose();
      toast.success(t('success.permissionsUpdated'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.savePermissions');
      alertDialog.error(t('errors.title'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = (enabled: boolean) => {
    if (formRef.current) {
      formRef.current.toggleAll(enabled);
    }
  };

  const handleFromStateChange = (hasChanges: boolean, isValid: boolean) => {
    setHasChanges(hasChanges);
    setIsValid(isValid);
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col" gapSize="12px">
          <Box flx="col">
            <span>{t('drawer.title')}</span>
            {emailAddress && <BodyText weight="md">{emailAddress}</BodyText>}
          </Box>
          <PermissionsSearchField value={searchTerm} onChange={setSearchTerm} />
        </Box>
      }
      footer={
        <Box jc="fs" gap="sm" className={styles.footer}>
          {!isSearching && (
            <Button
              onClick={() => handleToggleAll(!allEnabled)}
              colorScheme="secondary"
              className={styles.enableAllButton}
            >
              {allEnabled
                ? t('drawer.disableAllButton', { defaultValue: 'Disable All' })
                : t('drawer.enableAllButton', { defaultValue: 'Enable All' })}
            </Button>
          )}
          <Button
            disabled={loading || !hasChanges || !isValid}
            colorScheme="primary"
            className={styles.saveButton}
            form="accountUserPermissionsForm"
            type="submit"
          >
            {t('drawer.saveButton')}
          </Button>
        </Box>
      }
    >
      {!loading && dataReady && (
        <>
          {assignedRoles.length > 0 && (
            <div className={cn('rolesAssignedRow')}>
              <div className={cn('rolesAssignedLabel')}>
                <span>{t('drawer.rolesAssigned', { defaultValue: 'Role(s) Assigned' })}</span>
                <Tooltip
                  type="hover"
                  position="right"
                  hasButton={false}
                  customElement={
                    <MaterialSymbol
                      icon="info"
                      size="16px"
                      color="rgba(45, 45, 45, 0.5)"
                    />
                  }
                  information={t('drawer.rolesAssignedTooltip', {
                    defaultValue:
                      "Permissions from assigned roles appear as enabled and can't be toggled here. To remove them, unassign the role from Actions → Edit Role.",
                  })}
                />
              </div>
              <div className={cn('rolesAssignedChips')}>
                {assignedRoles.map((role) => (
                  <Chip key={role.RoleID} label={role.Name} />
                ))}
              </div>
            </div>
          )}
          <AccountUserPermissionsForm
            ref={formRef}
            id="accountUserPermissionsForm"
            searchTerm={searchTerm}
            onSubmit={handleSave}
            permissions={permissions}
            lockedPermissions={lockedPermissions}
            onFormStateChange={handleFromStateChange}
            onAllEnabledChange={setAllEnabled}
          />
        </>
      )}
    </SideDrawer>
  );
};

export default AccountUserPermissionsDrawer;
